import { createPayment } from "@/actions/payment";
import { staticContent } from "@/data/static-content";
import { FeedbackCategory, Gender, Image, PaymentMethod, PaymentStatus, Video } from "@/prisma/generated/browser";
import { getAffiliatesListForAdmin, getReferralsListForAdmin, getReferralsListForAffiliate } from "@/queries/dashboard";
import { getUser } from "@/queries/user";
import { InfiniteData } from "@tanstack/react-query";
import { ReactNode } from "react";
import { z } from "zod";
import { getAffiliate } from "../queries/affiliate";
import { getBlog, getBlogLikeStatus, getBlogsList, getBlogsListForTeacher } from "../queries/blog";
import { getChapter, getChapterExistenceStatus, getChaptersList, getChapterStats } from "../queries/chapter";
import { getComments } from "../queries/comment";
import {
  getCourse,
  getCourseChapterCompletionsForStudent,
  getCourseEnrollmentForStudent,
  getCoursePaymentAndReviewForStudent,
  getCoursePaymentForStudent,
  getCourseReviewForStudent,
  getCourseReviews,
  getCoursesList,
  getCoursesListForTeacher,
} from "../queries/course";
import {
  getEnrolledCoursesForStudent,
  getMostCompletedChaptersForTeacher,
  getMostEngagingStudentsForTeacher,
  getMostLikedBlogsForTeacher,
  getMostViewedChaptersForTeacher,
  getPaymentsChartDataForTeacher,
  getRecentBlogLikesForStudent,
  getRecentBlogLikesForTeacher,
  getRecentChapterCompletionsForStudent,
  getRecentChapterCompletionsForTeacher,
  getRecentChapterLikesForStudent,
  getRecentChapterViewsForStudent,
  getRecentChapterViewsForTeacher,
  getRecentEnrollmentsListForTeacher,
  getWatchTimeChartDataForStudent,
} from "../queries/dashboard";
import { getImages } from "../queries/image";
import { getLeaderboardList } from "../queries/leaderboard";
import { getUnreadCount, getUserNotifications } from "../queries/notification";
import { getPayment, getPaymentsForStudent, getPaymentsListForTeacher } from "../queries/payment";
import { getProfileDataForStudent } from "../queries/profile";
import { getStaticContent } from "../queries/static-content";
import { getMuxVideo, getMuxVideos } from "../queries/video";
import {
  activityOverviewChartMetricSchema,
  affiliateCreateSchema,
  affiliateDBSchema,
  anySchema,
  blogDBSchema,
  blogEditSchema,
  categoryCreateSchema,
  chapterDBSchema,
  columnDefSchema,
  courseContentSchema,
  courseDBSchema,
  courseEditSchema,
  courseReviewDBSchema,
  dataObjectSchema,
  datePresetSchema,
  deletedUserSchema,
  enrollmentFeatureAdditionalSchema,
  enrollmentFeatureSchema,
  faqSchema,
  featureFullSchema,
  featureSchema,
  fieldGroupSchema,
  genericFieldSchema,
  genericObjectSchema,
  idSchema,
  imageCreateSchema,
  landingPageContentSchema,
  localFileSchema,
  mockReviewCreateSchema,
  mockReviewDBSchema,
  newsletterSubscriptionSchema,
  originalFileSchema,
  paymentCreateSchema,
  paymentDBSchema,
  projectSchema,
  referralsChartMetricSchema,
  reviewSchema,
  sectionContent,
  sectionDBSchema,
  stepSchema,
  stringOrStringArraySchema,
  stringSchema,
  studentActivityOverviewChartMetricSchema,
  timeRangeSchema,
  timestampVariantSchema,
  uploadControllerSchema,
  uploadProgressSchema,
  uploadthingFilesArraySchema,
  uploadthingFileSchema,
  userSchema,
  videoCreateSchema,
} from "./zod-types";

// General

export type TCoursesList = Awaited<ReturnType<typeof getCoursesList>>;
export type TCourseListItem = Awaited<ReturnType<typeof getCoursesList>>[number];

export type TBlogsList = Awaited<ReturnType<typeof getBlogsList>>;
export type TBlogListItem = Awaited<ReturnType<typeof getBlogsList>>[number];

export type TLeaderboardList = Awaited<ReturnType<typeof getLeaderboardList>>;
export type TLeaderboardListItem = Awaited<ReturnType<typeof getLeaderboardList>>[number];

export type TChaptersList = Awaited<ReturnType<typeof getChaptersList>>;
export type TChapterListItem = Awaited<ReturnType<typeof getChaptersList>>[number];

export type TCourse = NonNullable<Awaited<ReturnType<typeof getCourse>>>;
export type TCourseSection = NonNullable<Awaited<ReturnType<typeof getCourse>>>["sections"][number];
export type TCourseChapter = TCourseSection["chapters"][number];
export type TChapter = NonNullable<Awaited<ReturnType<typeof getChapter>>>;
export type TChapterStats = NonNullable<Awaited<ReturnType<typeof getChapterStats>>>;
export type TBlog = NonNullable<Awaited<ReturnType<typeof getBlog>>>;
export type TPayment = NonNullable<Awaited<ReturnType<typeof getPayment>>>;
export type TMuxVideo = NonNullable<Awaited<ReturnType<typeof getMuxVideo>>>;
export type TStaticContentOld = NonNullable<Awaited<ReturnType<typeof getStaticContent>>>;
export type TUserReview = NonNullable<Awaited<ReturnType<typeof getCourseReviewForStudent>>>;
export type TAffiliate = NonNullable<Awaited<ReturnType<typeof getAffiliate>>>;
export type TCreatedPayment = Awaited<ReturnType<typeof createPayment>>;
export type TComment = Awaited<ReturnType<typeof getComments>>["comments"][number];
export type TOffering = Awaited<ReturnType<typeof getCourse>>["offerings"][number];
export type TReview = Awaited<ReturnType<typeof getCourseReviews>>["reviews"][number];
export type TUser = NonNullable<Awaited<ReturnType<typeof getUser>>>;

export type TBlogLikeStatus = Awaited<ReturnType<typeof getBlogLikeStatus>>;
export type TChapterExistenceStatus = Awaited<ReturnType<typeof getChapterExistenceStatus>>;

export type TImage = Image;
export type TVideo = Video;

export type TImages = Awaited<ReturnType<typeof getImages>>;
export type TMuxVideos = Awaited<ReturnType<typeof getMuxVideos>>;

export type TUserNotifications = InfiniteData<Awaited<ReturnType<typeof getUserNotifications>>>;
export type TUnreadCount = Awaited<ReturnType<typeof getUnreadCount>>;

export type TUploadedFile = TImage | TVideo | null;

// Admin Dashboard

export type TAffiliatesListForAdmin = Awaited<ReturnType<typeof getAffiliatesListForAdmin>>;
export type TReferralsListForAdmin = Awaited<ReturnType<typeof getReferralsListForAdmin>>;

// Teacher Dashboard

export type TCoursesListForTeacher = Awaited<ReturnType<typeof getCoursesListForTeacher>>;
export type TCourseListItemForTeacher = Awaited<ReturnType<typeof getCoursesListForTeacher>>[number];

export type TBlogsListForTeacher = Awaited<ReturnType<typeof getBlogsListForTeacher>>;
export type TBlogListItemForTeacher = Awaited<ReturnType<typeof getBlogsListForTeacher>>[number];

export type TPaymentsListForTeacher = Awaited<ReturnType<typeof getPaymentsListForTeacher>>;
export type TPaymentListItemForTeacher = Awaited<ReturnType<typeof getPaymentsListForTeacher>>[number];

export type TRecentChapterViewsForTeacher = Awaited<ReturnType<typeof getRecentChapterViewsForTeacher>>;
export type TRecentChapterCompletionsForTeacher = Awaited<ReturnType<typeof getRecentChapterCompletionsForTeacher>>;
export type TRecentBlogLikesForTeacher = Awaited<ReturnType<typeof getRecentBlogLikesForTeacher>>;
export type TRecentEnrollmentsListForTeacher = Awaited<ReturnType<typeof getRecentEnrollmentsListForTeacher>>;
// export type TRecentEnrollmentsForTeacher = Awaited<ReturnType<typeof getRecentEnrollments>>;

export type TMostViewedChaptersForTeacher = Awaited<ReturnType<typeof getMostViewedChaptersForTeacher>>;
export type TMostCompletedChaptersForTeacher = Awaited<ReturnType<typeof getMostCompletedChaptersForTeacher>>;
export type TMostLikedBlogsForTeacher = Awaited<ReturnType<typeof getMostLikedBlogsForTeacher>>;
export type TMostEngagingStudentsForTeacher = Awaited<ReturnType<typeof getMostEngagingStudentsForTeacher>>;
// export type TMostViewedChaptersByCourseIdForTeacher = Awaited<ReturnType<typeof getMostViewedChaptersByCourseId>>;
// export type TMostCompletedChaptersByCourseIdForTeacher = Awaited<ReturnType<typeof getMostCompletedChaptersByCourseId>>;

export type TPaymentsChartDataForTeacher = Awaited<ReturnType<typeof getPaymentsChartDataForTeacher>>;

export type TEnrollmentsByDatePresetForTeacher = { date: string; enrollmentsCount: number };
export type TCourseEnrollmentsByDatePresetForTeacher = { date: string; enrollmentsCount: number };

// Student dashboard

export type TProfileDataForStudent = Awaited<ReturnType<typeof getProfileDataForStudent>>;
export type TCourseEnrollmentForStudent = Awaited<ReturnType<typeof getCourseEnrollmentForStudent>>;
export type TCoursePaymentAndReviewForStudent = Awaited<ReturnType<typeof getCoursePaymentAndReviewForStudent>>;
export type TCoursePaymentForStudent = Awaited<ReturnType<typeof getCoursePaymentForStudent>>;
export type TCourseReviewForStudent = Awaited<ReturnType<typeof getCourseReviewForStudent>>;

export type TPaymentsListForStudent = Awaited<ReturnType<typeof getPaymentsForStudent>>;
export type TEnrolledCoursesForStudent = Awaited<ReturnType<typeof getEnrolledCoursesForStudent>>;
export type TCourseChapterCompletionsForStudent = Awaited<ReturnType<typeof getCourseChapterCompletionsForStudent>>;

export type TRecentChapterViewsForStudent = Awaited<ReturnType<typeof getRecentChapterViewsForStudent>>;
export type TRecentChapterCompletionsForStudent = Awaited<ReturnType<typeof getRecentChapterCompletionsForStudent>>;
export type TRecentChapterLikesForStudent = Awaited<ReturnType<typeof getRecentChapterLikesForStudent>>;
export type TRecentBlogLikesForStudent = Awaited<ReturnType<typeof getRecentBlogLikesForStudent>>;

export type TWatchTimeChartDataForStudent = Awaited<ReturnType<typeof getWatchTimeChartDataForStudent>>;

export type TChapterViewsByDatePresetForStudent = { date: string; chapterViewsCount: number };
export type TChapterCompletionsByDatePresetForStudent = { date: string; chapterCompletionsCount: number };
export type TWatchTimeByDatePresetForStudent = { date: string; duration: number };

// Affiliate Dashboard

export type TReferralsListForAffiliate = Awaited<ReturnType<typeof getReferralsListForAffiliate>>;

// Zod Inferred Types

export type ZIdSchema = z.infer<typeof idSchema>;
export type ZStringSchema = z.infer<typeof stringSchema>;
export type ZAnySchema = z.infer<typeof anySchema>;
export type ZStringOrStringArraySchema = z.infer<typeof stringOrStringArraySchema>;

export type ZAffiliateCreateSchema = z.infer<typeof affiliateCreateSchema>;

export type ZCourseDBSchema = z.infer<typeof courseDBSchema>;
export type ZCourseEditSchema = z.infer<typeof courseEditSchema>;
export type ZSectionDBSchema = z.infer<typeof sectionDBSchema>;
export type ZChapterDBSchema = z.infer<typeof chapterDBSchema>;
export type ZBlogDBSchema = z.infer<typeof blogDBSchema>;
export type ZBlogEditSchema = z.infer<typeof blogEditSchema>;
export type ZAffiliateDBSchema = z.infer<typeof affiliateDBSchema>;
export type ZNewsletterSubscriptionSchema = z.infer<typeof newsletterSubscriptionSchema>;
export type ZPaymentCreateSchema = z.infer<typeof paymentCreateSchema>;
export type ZPaymentDBSchema = z.infer<typeof paymentDBSchema>;
export type ZCourseReviewDBSchema = z.infer<typeof courseReviewDBSchema>;
export type ZCategoryCreateSchema = z.infer<typeof categoryCreateSchema>;
export type ZMockReviewCreateSchema = z.infer<typeof mockReviewCreateSchema>;
export type ZMockReviewDBSchema = z.infer<typeof mockReviewDBSchema>;

export type ZUserSchema = z.infer<typeof userSchema>;
export type ZDeletedUserSchema = z.infer<typeof deletedUserSchema>;

export type ZLocalFileSchema = z.infer<typeof localFileSchema>;
export type ZOriginalFileSchema = z.infer<typeof originalFileSchema>;
export type ZUploadthingFileSchema = z.infer<typeof uploadthingFileSchema>;
export type ZUploadthingFilesArraySchema = z.infer<typeof uploadthingFilesArraySchema>;
export type ZImageCreateSchema = z.infer<typeof imageCreateSchema>;
export type ZVideoCreateSchema = z.infer<typeof videoCreateSchema>;

export type ZGenericField = z.infer<typeof genericFieldSchema>;
export type ZFieldGroup = z.infer<typeof fieldGroupSchema>;

export type ZLandingPageContent = z.infer<typeof landingPageContentSchema>;
export type ZCourseContent = z.infer<typeof courseContentSchema>;

export type ZStep = z.infer<typeof stepSchema>;
export type ZProject = z.infer<typeof projectSchema>;
export type ZReview = z.infer<typeof reviewSchema>;
export type ZFeature = z.infer<typeof featureSchema>;
export type ZFeatureFull = z.infer<typeof featureFullSchema>;
export type ZFaq = z.infer<typeof faqSchema>;
export type ZEnrollmentFeature = z.infer<typeof enrollmentFeatureSchema>;
export type ZEnrollmentFeatureAdditional = z.infer<typeof enrollmentFeatureAdditionalSchema>;

export type ZDatePreset = z.infer<typeof datePresetSchema>;

export type ZActivityOverviewChartMetric = z.infer<typeof activityOverviewChartMetricSchema>;
export type ZStudentActivityOverviewChartMetric = z.infer<typeof studentActivityOverviewChartMetricSchema>;
export type ZReferralsChartMetric = z.infer<typeof referralsChartMetricSchema>;
export type ZTimeRange = z.infer<typeof timeRangeSchema>;
export type ZTimestampVariant = z.infer<typeof timestampVariantSchema>;

export type ZUploadProgress = z.infer<typeof uploadProgressSchema>;
export type ZUploadController = z.infer<typeof uploadControllerSchema>;

export type ZDataObject = z.infer<typeof dataObjectSchema>;
export type ZGenericObject = z.infer<typeof genericObjectSchema>;

export type ZColumnDef<T> = z.output<ReturnType<typeof columnDefSchema<T>>>;

// Unorganized

export type TStaticContent = typeof staticContent;

export type ZSectionContent = z.infer<typeof sectionContent>;

export type TFileMap = {
  image: TImage;
  video: TVideo;
};

export type TCourseVariant = (typeof staticContent.courses)[number]["variants"][number];
export type TCourseVariantId = (typeof staticContent.courses)[number]["variants"][number]["id"];

export type Response<T> = { success: true; data: T } | { success: false; error: string };

export type GeneralProps = {
  className?: string;
  children: ReactNode;
};

// Enums

export type TFileType = "image" | "video";
export type TContentType = "chapter" | "blog" | "course";

export const TPaymentMethodEnum = Object.values(PaymentMethod) as [PaymentMethod, ...PaymentMethod[]];
export const TPaymentStatusEnum = Object.values(PaymentStatus) as [PaymentStatus, ...PaymentStatus[]];
export const TFeedbackCategoryEnum = Object.values(FeedbackCategory) as [FeedbackCategory, ...FeedbackCategory[]];
export const TGenderEnum = Object.values(Gender) as [Gender, ...Gender[]];

// For "Select" Form Field
export const PAYMENT_METHODS = [
  { id: "EASYPAISA", title: "Easypaisa" },
  { id: "JAZZCASH", title: "JazzCash" },
  { id: "BANK_TRANSFER", title: "Bank Transfer" },
] as const;

export const FEEDBACK_CATEGORIES = [
  { id: "QUESTION", title: "Question" },
  { id: "PROBLEM", title: "Problem" },
  { id: "SUGGESTION", title: "Suggestion" },
  { id: "OTHER", title: "Other" },
] as const;

export const NOTIFICATION_TYPES_FOR_ADMIN = [{ id: "system_status_update", title: "System Status Update" }] as const;

export const NOTIFICATION_RECIPIENT_TYPES_FOR_ADMIN = [
  { id: "everyone", title: "Everyone" },
  { id: "individual", title: "Individual" },
] as const;

export const NOTIFICATION_TYPES_FOR_TEACHER = [
  { id: "course_published", title: "Course Published" },
  { id: "course_updated", title: "Course Updated" },
  { id: "blog_published", title: "Blog Published" },
  { id: "blog_updated", title: "Blog Updated" },
] as const;

export const NOTIFICATION_RECIPIENT_TYPES_FOR_TEACHER = [
  { id: "course_enrollees", title: "Course Enrollees" },
  { id: "followers", title: "Followers" },
] as const;

export const GENDER = [
  { id: "MALE", title: "Male" },
  { id: "FEMALE", title: "Female" },
] as const;

// Recycled

// export const TPaymentMethodEnum = PAYMENT_METHODS.map((method) => method.id) as [
//   (typeof PAYMENT_METHODS)[number]["id"],
//   ...(typeof PAYMENT_METHODS)[number]["id"][],
// ];

// export type TPaymentMethod = (typeof TPaymentMethodEnum)[number];

// 22 Apr 2025
// export type TCoursesListForDropdown = Awaited<ReturnType<typeof getCoursesListForDropdown>>;

// 15 Feb 2026

// export type TImage = Prisma.ImageGetPayload<{}>;
// export type TVideo = Prisma.VideoGetPayload<{}>;

// 18 Feb 2026

// export type TImage = Prisma.ImageGetPayload<object>;
